import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { LARGEST_PAGE } from '../api/paging';
import type { MessageAttachmentResponse } from '../api/types';
import { useApi, useMediaUrl } from '../auth/ApiProvider';
import { useI18n } from '../i18n/I18nProvider';
import { useLimits } from '../hooks/LimitsProvider';
import { toChatImage } from '../media/chatImage';
import {
  RecorderError, canPlayAudio, startVoiceRecording, type RecorderProblem, type Recording, type VoiceRecording,
} from '../media/voiceRecorder';
import { ChooseImageButton } from './Photo';
import { Button, Notice, Row, Stack } from './primitives';
import { noSave, noSavePlayer, noSaveVideo } from './noSave';
import { ProtectedVideo } from './ProtectedVideo';
import { Watermark } from './Watermark';
import { ErrorState, LoadingState } from './states';

/**
 * What a message carries besides its text: gallery photos and videos, voice notes, and pictures a guardian
 * sent. A guardian's picture opens in the same viewer as a gallery photo.
 *
 * The links are signed and expire within fifteen minutes, and the thread refreshes every few seconds, so a
 * link is always fresh enough to use and is never kept.
 */
export function MessageAttachments({ attachments }: { attachments: MessageAttachmentResponse[] }) {
  const { t } = useI18n();
  const resolve = useMediaUrl();
  const [viewing, setViewing] = useState<string | null>(null);

  if (attachments.length === 0) return null;

  return (
    <div className="dg-attachments">
      {attachments.map((attachment, index) => {
        const key = `${attachment.kind}-${attachment.photoId ?? index}`;

        if (!attachment.available || !attachment.url) {
          return <p key={key} className="dg-attachment--gone dg-muted dg-text-sm">{t('messages.unavailable')}</p>;
        }

        if (attachment.kind === 'audio') return <VoiceNotePlayer key={key} attachment={attachment} />;

        if (attachment.kind === 'video') {
          return (
            <ProtectedVideo
              key={key}
              className="dg-attachment"
              src={resolve(attachment.url) ?? attachment.url}
              type={attachment.contentType}
            />
          );
        }

        const url = resolve(attachment.url) ?? attachment.url;
        return (
          <button
            key={key}
            type="button"
            className="dg-attachment dg-attachment--photo"
            aria-label={t('messages.photo.open')}
            onClick={() => setViewing(url)}
          >
            <img {...noSave} src={resolve(attachment.thumbnailUrl) ?? url} alt="" loading="lazy" />
          </button>
        );
      })}

      {viewing && <PhotoViewer url={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

/**
 * A photo filling the screen, closed with the button or Escape. It sits above the page, and so above the
 * screen's watermark, so it draws its own (Q49).
 */
function PhotoViewer({ url, onClose }: { url: string; onClose: () => void }) {
  const { t } = useI18n();
  const close = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    close.current?.focus();
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="dg-viewer" role="dialog" aria-modal="true" aria-label={t('messages.photo.open')} onClick={onClose}>
      <img {...noSave} src={url} alt="" onClick={(event) => event.stopPropagation()} />
      <Watermark />
      <button ref={close} type="button" className="dg-button dg-button--secondary" onClick={onClose}>
        {t('action.close')}
      </button>
    </div>
  );
}

/**
 * A voice note: the browser's own player where it can play the format, and a note saying so where it cannot.
 *
 * Chrome records WebM and Safari records MP4, and I have not confirmed that every browser plays the other's
 * format (PLAN.md §13.4). Where it cannot, there is no download to fall back on (Q7), so the note says to
 * open the conversation in another browser.
 */
export function VoiceNotePlayer({ attachment }: { attachment: MessageAttachmentResponse }) {
  const { t, formatDuration } = useI18n();
  const resolve = useMediaUrl();
  const time = formatDuration(attachment.durationMs ?? 0);
  const url = resolve(attachment.url);

  if (!canPlayAudio(attachment.contentType)) {
    return <p className="dg-muted dg-text-sm">{t('messages.voice.unplayable', { time })}</p>;
  }

  return (
    <Row>
      <audio
        {...noSavePlayer}
        className="dg-voice-note"
        controls
        preload="metadata"
        src={url}
        aria-label={t('messages.voice.label', { time })}
      />
      <span className="dg-muted dg-text-sm">{time}</span>
    </Row>
  );
}

type RecorderState =
  | { kind: 'idle' }
  | { kind: 'starting' }
  | { kind: 'recording'; recording: VoiceRecording; startedAt: number };

type RecorderNotice = RecorderProblem | 'ready' | 'tooShort' | 'limit';

const NOTICE_KEYS: Record<RecorderNotice, string> = {
  unsupported: 'messages.voice.unsupported',
  denied: 'messages.voice.denied',
  noMicrophone: 'messages.voice.noMicrophone',
  failed: 'messages.voice.failed',
  ready: 'messages.voice.ready',
  tooShort: 'messages.voice.tooShort',
  limit: 'messages.voice.limit',
};

/**
 * Hold to record, release to send (decision 2026-09-23 #22). Space or Enter held down does the same, so the
 * button works without a pointer.
 *
 * The first press on a site opens the browser's microphone prompt, and answering it means letting go of
 * the button. A press released before the microphone is ready is therefore discarded and explained, rather
 * than turned into an empty message.
 */
export function VoiceNoteButton({ onRecorded, disabled }: {
  onRecorded: (recording: Recording) => void;
  disabled?: boolean;
}) {
  const { t, formatDuration } = useI18n();
  const { chat } = useLimits();
  const [state, setState] = useState<RecorderState>({ kind: 'idle' });
  const [notice, setNotice] = useState<RecorderNotice | null>(null);
  const [elapsed, setElapsed] = useState(0);
  // What the handlers read. Kept in step by `update` rather than by rendering, because a finger lifted a
  // moment after it pressed arrives before React has rendered the press.
  const stateRef = useRef(state);
  const update = (next: RecorderState) => {
    stateRef.current = next;
    setState(next);
  };
  // Set when the button is let go while the microphone prompt is still open.
  const releasedEarly = useRef(false);

  useEffect(() => {
    if (state.kind !== 'recording') return;
    const timer = setInterval(() => setElapsed(performance.now() - state.startedAt), 250);
    return () => clearInterval(timer);
  }, [state]);

  // Leaving the screen mid-recording throws the recording away and turns the microphone off.
  useEffect(() => () => {
    if (stateRef.current.kind === 'recording') stateRef.current.recording.cancel();
  }, []);

  async function begin() {
    if (stateRef.current.kind !== 'idle' || disabled) return;
    setNotice(null);
    setElapsed(0);
    releasedEarly.current = false;
    update({ kind: 'starting' });

    let recording: VoiceRecording;
    try {
      recording = await startVoiceRecording(chat.voiceNoteMaximumMs);
    } catch (error) {
      update({ kind: 'idle' });
      setNotice(error instanceof RecorderError ? error.problem : 'failed');
      return;
    }

    if (releasedEarly.current) {
      // Let go while the prompt was open: nothing was said yet, and the next press will record at once.
      recording.cancel();
      update({ kind: 'idle' });
      setNotice('ready');
      return;
    }

    update({ kind: 'recording', recording, startedAt: performance.now() });

    const result = await recording.finished;
    update({ kind: 'idle' });
    if (!result) {
      setNotice((current) => current ?? 'tooShort');
      return;
    }
    if (result.reachedLimit) setNotice('limit');
    onRecorded(result);
  }

  function release() {
    const current = stateRef.current;
    if (current.kind === 'starting') releasedEarly.current = true;
    if (current.kind === 'recording') void current.recording.stop();
  }

  function cancel() {
    const current = stateRef.current;
    if (current.kind === 'recording') {
      setNotice('tooShort');
      current.recording.cancel();
    }
  }

  const onPointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    // Keeps the release on this button even when the finger drifts off it.
    event.currentTarget.setPointerCapture?.(event.pointerId);
    void begin();
  };

  const isHoldKey = (event: KeyboardEvent) => event.key === ' ' || event.key === 'Enter';

  return (
    <Stack tight>
      <button
        type="button"
        className={`dg-button dg-button--secondary dg-voice-button${state.kind === 'recording' ? ' dg-voice-button--live' : ''}`}
        disabled={disabled}
        aria-pressed={state.kind === 'recording'}
        onPointerDown={onPointerDown}
        onPointerUp={release}
        onPointerCancel={cancel}
        onContextMenu={(event) => event.preventDefault()}
        onKeyDown={(event) => {
          if (!isHoldKey(event)) return;
          event.preventDefault();
          if (!event.repeat) void begin();
        }}
        onKeyUp={(event) => {
          if (!isHoldKey(event)) return;
          event.preventDefault();
          release();
        }}
      >
        {state.kind === 'recording'
          ? t('messages.voice.release', { time: formatDuration(elapsed) })
          : t('messages.voice.hold')}
      </button>
      {notice
        ? (
          <Notice tone={notice === 'ready' ? 'info' : 'warning'}>
            {t(NOTICE_KEYS[notice], { duration: formatDuration(chat.voiceNoteMaximumMs) })}
          </Notice>
        )
        : <p className="dg-muted dg-text-sm">{t('messages.voice.hint')}</p>}
    </Stack>
  );
}

/**
 * A guardian's picture from their phone (Q5, Q46): a prescription, a rash. Chosen, shrunk and re-encoded as
 * JPEG here, which also drops its location, then handed on. Staff never see this; they share from the
 * gallery, where the photo consents apply.
 */
export function ChatImageButton({ busy, onChosen }: {
  busy: boolean;
  onChosen: (image: Blob) => void;
}) {
  const { t, formatMegabytes } = useI18n();
  const { chat } = useLimits();
  const [notice, setNotice] = useState<'unreadable' | 'tooLarge' | null>(null);

  const choose = (image: Blob) => {
    // The API would refuse it too; saying so here spares an upload over a slow connection.
    if (image.size > chat.imageMaximumBytes) {
      setNotice('tooLarge');
      return;
    }
    setNotice(null);
    onChosen(image);
  };

  return (
    <Stack tight>
      <ChooseImageButton
        label={t('messages.image.send')}
        busy={busy}
        prepare={(file) => toChatImage(file, chat.imageMaximumEdge)}
        onChosen={choose}
        onUnreadable={() => setNotice('unreadable')}
      />
      {notice
        ? (
          <Notice tone="warning">
            {notice === 'tooLarge'
              ? t('messages.image.tooLarge', { mb: formatMegabytes(chat.imageMaximumBytes) })
              : t('messages.image.unreadable')}
          </Notice>
        )
        : <p className="dg-muted dg-text-sm">{t('messages.image.hint')}</p>}
    </Stack>
  );
}

/**
 * The staff picker for sharing gallery items into a conversation about a child.
 *
 * It offers only what the server says may be shared here, which is the child's published items that every
 * family in the thread may already see, so the choice cannot include something the send would refuse.
 */
export function SharePicker({ conversationId, busy, onShare, onCancel }: {
  conversationId: number;
  busy: boolean;
  onShare: (photoIds: number[]) => void;
  onCancel: () => void;
}) {
  const api = useApi();
  const resolve = useMediaUrl();
  const { t, formatNumber } = useI18n();
  const { chat } = useLimits();
  const [chosen, setChosen] = useState<number[]>([]);

  const media = useQuery({
    queryKey: ['shareable-media', conversationId],
    queryFn: () => api.conversations.shareableMedia(conversationId, { pageSize: LARGEST_PAGE }),
  });

  if (media.isPending) return <LoadingState />;
  if (media.isError) return <ErrorState error={media.error} onRetry={() => void media.refetch()} />;
  if (media.data.items.length === 0) {
    return (
      <Stack tight>
        <Notice tone="info">{t('messages.share.empty')}</Notice>
        <Button variant="ghost" onClick={onCancel}>{t('action.cancel')}</Button>
      </Stack>
    );
  }

  const toggle = (photoId: number) => setChosen((current) => current.includes(photoId)
    ? current.filter((id) => id !== photoId)
    : current.length < chat.attachmentsMaximumPerMessage ? [...current, photoId] : current);

  return (
    <Stack tight>
      <div className="dg-photo-grid">
        {media.data.items.map((item) => (
          <label key={item.photoId} className="dg-share-item">
            <input
              type="checkbox"
              checked={chosen.includes(item.photoId)}
              disabled={!chosen.includes(item.photoId) && chosen.length >= chat.attachmentsMaximumPerMessage}
              onChange={() => toggle(item.photoId)}
            />
            {item.kind === 'Video'
              ? <video {...noSaveVideo} src={`${resolve(item.url)}#t=0.1`} preload="metadata" muted playsInline aria-label={item.caption ?? t('photos.video')} />
              : <img {...noSave} src={resolve(item.thumbnailUrl ?? item.url)} alt={item.caption ?? ''} loading="lazy" />}
          </label>
        ))}
      </div>
      {chosen.length >= chat.attachmentsMaximumPerMessage && (
        <p className="dg-muted dg-text-sm">
          {t('messages.share.limit', { max: formatNumber(chat.attachmentsMaximumPerMessage) })}
        </p>
      )}
      <Row>
        <Button disabled={chosen.length === 0} busy={busy} busyLabel={t('action.saving')} onClick={() => onShare(chosen)}>
          {t('messages.share.send', { count: chosen.length, max: formatNumber(chat.attachmentsMaximumPerMessage) })}
        </Button>
        <Button variant="ghost" disabled={busy} onClick={onCancel}>{t('action.cancel')}</Button>
      </Row>
    </Stack>
  );
}
